import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { MessageCircle, X, Send, User, Bot } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ChatbotWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { id: 1, text: "Welcome to BITZ Club! I can help you with membership plans, offers, partner details, and more. How can I help?", sender: 'bot' }
  ]);
  const [input, setInput] = useState('');
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadData, setLeadData] = useState({ name: '', mobile: '' });
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (text) => {
    if (!text.trim()) return;
    
    const userMsg = { id: Date.now(), text, sender: 'user' };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setSending(true);

    try {
      const res = await axios.post(`${API}/chatbot/message`, {
        message: text,
        name: leadData.name,
        mobile: leadData.mobile
      });
      
      const botMsg = { id: Date.now() + 1, text: res.data.response, sender: 'bot' };
      setMessages(prev => [...prev, botMsg]);
      
      // After 3 messages, prompt for contact info if not already given
      if (messages.length >= 4 && !leadData.mobile) {
        setTimeout(() => {
          setShowLeadForm(true);
        }, 1000);
      }
    } catch (err) {
      setMessages(prev => [...prev, { id: Date.now() + 1, text: "Sorry, something went wrong. Please try again.", sender: 'bot' }]);
    } finally {
      setSending(false);
    }
  };

  const submitLead = async () => {
    if (!leadData.name || !leadData.mobile || leadData.mobile.length < 10) return;
    
    setShowLeadForm(false);
    await sendMessage(`My name is ${leadData.name}, contact me at ${leadData.mobile}`);
  };

  const quickReplies = ['Membership Plans', 'Offers', 'Contact', 'How to Join?'];

  return (
    <>
      {/* Chat Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            onClick={() => setIsOpen(true)}
            data-testid="chatbot-open-btn"
            className="fixed bottom-20 right-4 z-50 w-14 h-14 bg-[#D4AF37] rounded-full flex items-center justify-center shadow-lg hover:bg-[#B8962E] transition-colors"
            style={{ boxShadow: '0 4px 20px rgba(212,175,55,0.4)' }}
          >
            <MessageCircle className="w-6 h-6 text-black" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            data-testid="chatbot-window"
            className="fixed bottom-4 right-4 z-50 w-[360px] max-w-[calc(100vw-2rem)] h-[500px] max-h-[calc(100vh-2rem)] bg-[#0F0F10] rounded-2xl shadow-2xl border border-gray-800 flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-[#D4AF37] to-[#B8962E] p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-black/20 rounded-full flex items-center justify-center">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="text-black font-bold text-sm">BITZ Club Assistant</h4>
                  <p className="text-black/60 text-xs">Online</p>
                </div>
              </div>
              <button onClick={() => setIsOpen(false)} data-testid="chatbot-close-btn" className="text-black/70 hover:text-black">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map(msg => (
                <div key={msg.id} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm whitespace-pre-line ${
                    msg.sender === 'user'
                      ? 'bg-[#D4AF37] text-black rounded-br-md'
                      : 'bg-[#1A1A1C] text-gray-200 rounded-bl-md border border-gray-800'
                  }`}>
                    {msg.text}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="bg-[#1A1A1C] text-gray-400 px-4 py-2.5 rounded-2xl rounded-bl-md text-sm border border-gray-800">
                    <span className="animate-pulse">Typing...</span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Quick Replies */}
            {messages.length <= 2 && (
              <div className="px-4 pb-2 flex flex-wrap gap-2">
                {quickReplies.map(qr => (
                  <button key={qr} onClick={() => sendMessage(qr)}
                    className="px-3 py-1.5 bg-[#D4AF37]/10 text-[#D4AF37] rounded-full text-xs hover:bg-[#D4AF37]/20 border border-[#D4AF37]/30">
                    {qr}
                  </button>
                ))}
              </div>
            )}

            {/* Lead Form */}
            {showLeadForm && (
              <div className="px-4 pb-2 bg-[#1A1A1C] border-t border-gray-800 p-3 mx-2 mb-2 rounded-xl">
                <p className="text-[#D4AF37] text-xs font-medium mb-2">Want us to call you back? Share your details:</p>
                <div className="space-y-2">
                  <input type="text" placeholder="Your Name" value={leadData.name}
                    onChange={e => setLeadData({...leadData, name: e.target.value})}
                    className="w-full px-3 py-2 bg-black/40 border border-gray-700 rounded-lg text-white text-sm outline-none focus:border-[#D4AF37]" />
                  <input type="tel" placeholder="Mobile Number" value={leadData.mobile}
                    onChange={e => setLeadData({...leadData, mobile: e.target.value.replace(/\D/g,'').slice(0,10)})}
                    className="w-full px-3 py-2 bg-black/40 border border-gray-700 rounded-lg text-white text-sm outline-none focus:border-[#D4AF37]" />
                  <button onClick={submitLead} className="w-full py-2 bg-[#D4AF37] text-black rounded-lg text-sm font-medium hover:bg-[#B8962E]">
                    Request Callback
                  </button>
                </div>
              </div>
            )}

            {/* Input */}
            <div className="p-3 border-t border-gray-800">
              <form onSubmit={e => { e.preventDefault(); sendMessage(input); }} className="flex gap-2">
                <input type="text" value={input} onChange={e => setInput(e.target.value)}
                  placeholder="Type your message..."
                  data-testid="chatbot-input"
                  className="flex-1 px-4 py-2.5 bg-[#1A1A1C] border border-gray-700 rounded-xl text-white text-sm outline-none focus:border-[#D4AF37]" />
                <button type="submit" disabled={!input.trim() || sending}
                  data-testid="chatbot-send-btn"
                  className="w-10 h-10 bg-[#D4AF37] rounded-xl flex items-center justify-center hover:bg-[#B8962E] disabled:opacity-50 transition-colors">
                  <Send className="w-4 h-4 text-black" />
                </button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatbotWidget;
